/**
 * Helper partagé pour la redirection multi-tenant URL-first via SSO handoff
 * (cf docs/multitenancy.md § Switch tenant et SSO handoff).
 *
 * Centralise le couple `auth.createHandoff` → `window.location.assign(handoff_url)`
 * utilisé à 3 endroits (Login post-success, ChoisirDomaine post-choix,
 * TenantSwitcher post-bascule).
 *
 * Pourquoi un helper plutôt que dupliquer : la séquence (créer le token côté
 * hub puis quitter le hub vers le sous-domaine) est sensible — un oubli
 * (par exemple `navigate()` au lieu de `window.location.assign()`) garderait
 * le user sur le hub et tous ses calls tRPC métier échoueraient. On garde
 * l'invariant `assign(handoff_url)` à un seul endroit.
 *
 * Note dev : en dev, le frontend (`localhost:5173`) parle à l'API
 * (`localhost:3001`) cross-origin. Le `Host` HTTP côté API est toujours
 * `localhost:3001` (= scope hub côté résolveur tenant), donc le multi-tenant
 * URL-first est partiellement testable en local — on vérifie juste que la
 * redirection vers `<slug>.localhost:5173/auth/handoff?token=...` se fait et
 * que le consume réussit. En prod (front + API même host), l'isolation
 * cookie hub/tenant est réellement effective.
 */

import { trpc } from "./trpc.js";

/**
 * Crée un token SSO handoff pour `domaineId` et redirige le navigateur vers
 * `<slug>.crayea.fr/auth/handoff?token=XXX` (ou l'équivalent dev).
 *
 * Utilise `window.location.assign()` (pas `navigate()`) car on quitte le host
 * courant pour atterrir sur un sous-domaine différent — React Router ne peut
 * pas franchir cette frontière.
 *
 * En cas d'erreur (membership inactive, domaine archivé, réseau), propage
 * l'exception au caller qui décide du fallback UI (toast, message d'erreur,
 * redirection /aucun-domaine, etc.).
 */
export async function redirectToTenantViaHandoff(domaineId: string): Promise<never> {
  const result = await trpc.auth.createHandoff.mutate({ target_domaine_id: domaineId });
  // assign() (vs href=) garde la page courante dans l'historique navigateur ;
  // si l'user fait "Back" il revient sur le hub plutôt que sortir du site.
  window.location.assign(result.handoff_url);
  // La page va décharger : on bloque la suite côté caller via Promise jamais
  // résolue. Typage `never` pour signaler que rien après cet appel ne tourne.
  return new Promise<never>(() => {});
}
